import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Filter, ChevronLeft, ChevronRight, ChevronDown, Trash2, Tag, Search as SearchIcon } from 'lucide-react';
import api from '../api';
import DateRangeBar from '../components/DateRangeBar';

const ACTION_LABELS = {
  login:                  'Login',
  login_failed:           'Login Failed',
  password_changed:       'Password Changed',
  parcel_created:         'Parcel Created',
  parcel_cancelled:       'Parcel Cancelled',
  parcel_scan:            'Parcel Scanned',
  parcel_assigned:        'Driver Assigned',
  scan_driver_corrected:  'Scan Driver Corrected',
  scan_dated:             'Scan Re-dated',
  driver_pay_manual_set:  'Driver Pay Manual Amount',
  parcel_pickup_assigned: 'Pickup Driver Assigned',
  parcel_measured:        'Parcel Measured',
  parcel_bill_to_changed: 'Bill-To Changed',
  parcel_weight_updated:  'Weight Updated',
  parcel_repriced:        'Re-priced on Invoice',
  parcel_invoiced:        'Parcel Invoiced',
  parcel_invoice_issued:  'Parcel Invoice Issued',
  parcel_released_from_invoice: 'Parcel Off Invoice',
  parcel_parties_changed: 'Sender/Recipient Changed',
  parcel_address_changed: 'Parcel Address Changed',
  parcel_notes_updated:   'Parcel Notes Updated',
  parcel_extra_added:     'Extra Item Added',
  parcel_extra_removed:   'Extra Item Removed',
  parcel_boxes_added:     'Boxes Added',
  parcel_box_removed:     'Box Removed',
  box_status_changed:     'Box Status Changed',
  proof_recorded:         'Proof Recorded',
  proof_sent:             'Proof Sent',
  parcels_bulk_assigned:  'Drivers Bulk Assigned',
  clients_merged:         'Clients Merged',
  client_created:         'Client Created',
  client_updated:         'Client Updated',
  client_keywords_updated:'Client Keywords Updated',
  quickbooks_import:      'QuickBooks Import',
  user_created:           'User Created',
  user_updated:           'User Updated',
  user_password_reset:    'User Password Reset',
  settings_updated:       'Settings Updated',
  safety_settings_updated:'Safety Settings Updated',
  billing_settings_updated:'Billing Settings Updated',
  template_updated:       'Template Updated',
  // financial
  invoice_created:        'Invoice Created',
  invoice_cancelled:      'Invoice Cancelled',
  credit_note_issued:     'Credit Note Issued',
  payment_recorded:       'Payment Recorded',
  payment_deleted:        'Payment Deleted',
  cash_expense_added:     'Petty Cash Added',
  cash_expense_deleted:   'Petty Cash Deleted',
  cashup_saved:           'Cash-up Saved',
  debt_note_added:        'Debt Note Added',
  database_parcels_cleared:'Database Cleared',
  bank_statement_imported:'Bank Statement Imported',
  bank_transaction_matched:'Bank Txn Matched',
  pastel_exported:        'Pastel Export',
  statement_emailed:      'Statement Emailed',
  sos_raised:             'SOS Raised',
  sos_resolved:           'SOS Resolved',
};

// Destructive / reversal actions — shown in red and grouped by the Deletions filter.
const DELETION_ACTIONS = [
  'payment_deleted', 'invoice_cancelled', 'credit_note_issued', 'parcel_cancelled',
  'cash_expense_deleted', 'database_parcels_cleared',
];

const ACTION_COLORS = {
  login:                  'bg-green-100 text-green-700',
  login_failed:           'bg-red-100 text-red-700',
  password_changed:       'bg-yellow-100 text-yellow-700',
  parcel_created:         'bg-blue-100 text-blue-700',
  parcel_scan:            'bg-purple-100 text-purple-700',
  parcel_assigned:        'bg-orange-100 text-orange-700',
  parcel_pickup_assigned: 'bg-orange-100 text-orange-700',
  parcels_bulk_assigned:  'bg-orange-100 text-orange-700',
  parcel_measured:        'bg-emerald-100 text-emerald-700',
  parcel_weight_updated:  'bg-emerald-100 text-emerald-700',
  parcel_repriced:        'bg-emerald-100 text-emerald-700',
  parcel_invoiced:        'bg-emerald-100 text-emerald-700',
  parcel_invoice_issued:  'bg-emerald-100 text-emerald-700',
  parcel_bill_to_changed: 'bg-emerald-100 text-emerald-700',
  parcel_parties_changed: 'bg-sky-100 text-sky-700',
  parcel_address_changed: 'bg-sky-100 text-sky-700',
  parcel_released_from_invoice: 'bg-amber-100 text-amber-700',
  scan_dated:             'bg-amber-100 text-amber-700',
  client_created:         'bg-teal-100 text-teal-700',
  client_updated:         'bg-teal-100 text-teal-700',
  quickbooks_import:      'bg-teal-100 text-teal-700',
  user_created:           'bg-indigo-100 text-indigo-700',
  user_updated:           'bg-indigo-100 text-indigo-700',
  invoice_created:        'bg-blue-100 text-blue-700',
  payment_recorded:       'bg-green-100 text-green-700',
  sos_raised:             'bg-red-100 text-red-700',
  settings_updated:       'bg-gray-100 text-gray-700',
  template_updated:       'bg-gray-100 text-gray-700',
  // destructive → red
  ...Object.fromEntries(DELETION_ACTIONS.map((a) => [a, 'bg-red-100 text-red-700'])),
};

function DetailChip({ details }) {
  if (!details) return null;
  // `via` (web / app) is shown as its own marker next to the user, not as a chip
  const entries = Object.entries(details).filter(([k, v]) => k !== 'via' && v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {entries.map(([k, v]) => (
        <span key={k} className="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
          {k}: <span className="font-medium">{String(v)}</span>
        </span>
      ))}
    </div>
  );
}

const PAGE_SIZE = 50;

// Extra words that should find an action. The label doesn't always contain the
// word people search for — pricing is logged as "Parcel Measured", so typing
// "price" would otherwise find nothing.
const ACTION_KEYWORDS = {
  parcel_measured:          'price pricing priced manual weigh weight measure volumetric tier',
  invoice_created:          'bill billing tax invoice',
  invoice_cancelled:        'void cancel delete',
  credit_note_issued:       'refund reverse credit void',
  payment_recorded:         'paid receipt money received cash card eft',
  payment_deleted:          'refund remove delete money',
  cash_expense_added:       'petty cash expense purchase',
  cash_expense_deleted:     'petty cash expense delete',
  cashup_saved:             'till drawer balance cash-up',
  debt_note_added:          'debtor collection promise arrears',
  parcel_cancelled:         'void delete',
  parcel_scan:              'status collected delivered depot scanned',
  parcel_bill_to_changed:   'payer who pays billing',
  parcel_invoiced:          'bill billing invoice parcel',
  parcel_released_from_invoice: 'unbilled off invoice released credit cancel',
  parcel_parties_changed:   'sender recipient client changed merge',
  parcel_weight_updated:    'weight kg driver pay',
  parcel_assigned:          'driver allocate',
  scan_driver_corrected:    'driver scan credit wrong driver corrected re-credited',
  scan_dated:               'backdate back-date date time delivered moved booking',
  driver_pay_manual_set:    'driver pay manual amount contractor pcs override',
  quickbooks_import:        'import qb migration clients',
  bank_statement_imported:  'capitec bank import statement',
  bank_transaction_matched: 'bank match reconcile allocate',
  statement_emailed:        'email statement month-end send',
  database_parcels_cleared: 'delete wipe clear reset',
  login_failed:             'security failed password',
  user_password_reset:      'security password',
  sos_raised:               'emergency panic driver safety',
  clients_notify_bulk:      'notification email whatsapp telegram master switch',
};

// Type-to-search picker for the action filter — the list runs to ~35 actions,
// so scrolling a plain <select> to find one is slow. Matches on the friendly
// label, the raw action name, AND the synonyms above.
function ActionPicker({ value, actions, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = React.useRef(null);

  React.useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const label = value === 'all' ? 'All actions' : (ACTION_LABELS[value] || value);
  const needle = q.trim().toLowerCase();
  const shown = actions.filter((a) =>
    !needle || a.toLowerCase().includes(needle)
    || (ACTION_LABELS[a] || '').toLowerCase().includes(needle)
    || (ACTION_KEYWORDS[a] || '').includes(needle));

  const pick = (a) => { onChange(a); setOpen(false); setQ(''); };

  return (
    <div className="relative" ref={boxRef}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 border rounded-lg px-3 py-1.5 text-xs min-w-44 justify-between ${
          value !== 'all' ? 'border-orange-400 text-gray-800' : 'text-gray-600'
        }`}>
        <span className="truncate">{label}</span>
        <ChevronDown size={13} className="text-gray-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-72 bg-white border rounded-lg shadow-lg">
          <div className="p-2 border-b">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Type to find a filter…"
              className="w-full border rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            <button type="button" onClick={() => pick('all')}
              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-orange-50 ${value === 'all' ? 'text-orange-600 font-medium' : 'text-gray-700'}`}>
              All actions
            </button>
            {shown.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">No filter matches “{q}”</p>}
            {shown.map((a) => (
              <button key={a} type="button" onClick={() => pick(a)}
                className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-orange-50 ${value === a ? 'text-orange-600 font-medium' : 'text-gray-700'}`}>
                {ACTION_LABELS[a] || a}
                {ACTION_LABELS[a] && <span className="text-gray-300 ml-1.5">{a}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ActivityLog() {
  const today = new Date().toISOString().split('T')[0];
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  // ?search=OFF0004 lets other pages deep-link into this log (e.g. "full history"
  // on a parcel). Arriving with a search widens the window so old entries show.
  const [params] = useSearchParams();
  const initialSearch = params.get('search') || '';
  const [from, setFrom] = useState(initialSearch ? '2025-01-01' : sevenDaysAgo);
  const [to, setTo] = useState(today);
  const [action, setAction] = useState('all');
  const [category, setCategory] = useState('');   // '' | 'deletions' | 'manual_prices'
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(0);

  const { data: actionTypes = [] } = useQuery({
    queryKey: ['audit-actions'],
    queryFn: () => api.get('/audit-logs/actions').then((r) => r.data),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', from, to, action, category, search, page],
    queryFn: () =>
      api.get('/audit-logs', {
        params: {
          from, to,
          action: action !== 'all' ? action : undefined,
          category: category && action === 'all' ? category : undefined,
          search: search.trim() || undefined,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        },
      }).then((r) => r.data),
    keepPreviousData: true,
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <ClipboardList size={22} className="text-orange-500" />
        <h1 className="text-xl font-bold text-gray-800">Activity Log</h1>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} onChange={() => setPage(0)} />

        <div className="flex items-center gap-3 flex-wrap">
          <Filter size={14} className="text-gray-400" />
          <ActionPicker
            value={action}
            actions={actionTypes}
            onChange={(a) => { setAction(a); setCategory(''); setPage(0); }}
          />
          <button
            onClick={() => { setCategory((c) => (c === 'deletions' ? '' : 'deletions')); setAction('all'); setPage(0); }}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              category === 'deletions'
                ? 'bg-red-500 border-red-500 text-white'
                : 'border-gray-300 text-gray-600 hover:border-red-400'
            }`}>
            <Trash2 size={13} /> Deletions &amp; voids
          </button>
          <button
            onClick={() => { setCategory((c) => (c === 'manual_prices' ? '' : 'manual_prices')); setAction('all'); setPage(0); }}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              category === 'manual_prices'
                ? 'bg-amber-500 border-amber-500 text-white'
                : 'border-gray-300 text-gray-600 hover:border-amber-400'
            }`}>
            <Tag size={13} /> Manual prices
          </button>
          <div className="relative">
            <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search name, invoice, reason…"
              className="border rounded-lg pl-7 pr-3 py-1.5 text-xs w-56 focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <span className="text-xs text-gray-400 ml-auto">
            {total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-10 text-center text-gray-400">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-400">No activity in this period</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left whitespace-nowrap">Time</th>
                    <th className="px-4 py-3 text-left">User</th>
                    <th className="px-4 py-3 text-left">Action</th>
                    <th className="px-4 py-3 text-left">Entity</th>
                    <th className="px-4 py-3 text-left">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                        {new Date(row.created_at).toLocaleString('en-ZA', {
                          dateStyle: 'short', timeStyle: 'short',
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800 text-xs">
                          {row.user_name || '—'}
                          {row.details?.via && (
                            <span className={`ml-1.5 text-[10px] uppercase tracking-wide px-1.5 py-px rounded font-normal ${row.details.via === 'app' ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}>
                              {row.details.via === 'app' ? 'app' : 'web'}
                            </span>
                          )}
                        </p>
                        {row.ip && <p className="text-xs text-gray-400">{row.ip}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-xs px-2 py-1 rounded-full font-medium ${ACTION_COLORS[row.action] || 'bg-gray-100 text-gray-600'}`}>
                          {ACTION_LABELS[row.action] || row.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {row.entity_id ? (
                          <div>
                            <p className="text-xs text-gray-500">{row.entity_type}</p>
                            {row.entity_type === 'parcel' ? (
                              <a href={`/parcels/${row.entity_id}`}
                                className="text-xs font-mono font-semibold text-orange-500 hover:underline">
                                {row.entity_id}
                              </a>
                            ) : (
                              <p className="text-xs font-medium text-gray-700 font-mono truncate max-w-[120px]">
                                {row.entity_id}
                              </p>
                            )}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <DetailChip details={row.details} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <p className="text-xs text-gray-500">
                  Page {page + 1} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                    className="p-1.5 rounded border disabled:opacity-40 hover:bg-white">
                    <ChevronLeft size={14} />
                  </button>
                  <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                    className="p-1.5 rounded border disabled:opacity-40 hover:bg-white">
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
